import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './OnboardingPage.css';
import { GoogleLogin } from '@react-oauth/google';
import { useWoodpeckerAuth } from './Woodpecker';
import { FaCrown, FaExclamationCircle, FaTrash, FaChessQueen } from 'react-icons/fa';
import WoodpeckerMethodContent from './WoodpeckerMethodContent';
import woodpeckerSeoContent from './woodpeckerSeoContent.json';
import Modal from './Modal';
import PricingModal from './PricingModal';
import LegalModal from './LegalModal';
import TermsOfService from './legal/TermsOfService';
import PrivacyPolicy from './legal/PrivacyPolicy';
import { Link } from 'react-router-dom';
import { ReactComponent as LichessLogo } from './assets/lichessLogo.svg';
import { lichessPuzzleApi } from '../api';
import { v4 as uuidv4 } from 'uuid';
import { Helmet } from "react-helmet";
import { getQuantityValidation, getRatingError } from './onboardingValidation';
import { Button } from '../components/ui/button';

const inputClassName = 'block min-h-12 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 placeholder-slate-400 shadow-sm focus:border-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-700';

function OnboardingPage() {
  const {
    idToken,
    authProvider,
    isAuthenticated,
    handleGoogleLoginError,
    handleGoogleLoginSuccess,
    userEmail,
    userName,
    isPremium,
    startLichessOAuth,
    puzzleData,
    setPuzzleData,
    isPuzzleDataLoading,
  } = useWoodpeckerAuth();

  const freeMaxPuzzles = 100;
  const freeMaxSets = 1;

  const [rating, setRating] = useState('');
  const [puzzleQuantity, setPuzzleQuantity] = useState(50);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showQuantityTooltip, setShowQuantityTooltip] = useState(false);
  const [showSetLimitTooltip, setShowSetLimitTooltip] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const [quantityError, setQuantityError] = useState('');
  const [setName, setSetName] = useState('');
  const [selectedSetId, setSelectedSetId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();

  // Extract first name from full name
  const firstName = userName ? userName.split(' ')[0] : '';

  const handleUpgradeClick = () => {
    setShowPricingModal(true);
  };

  const validateRating = (value) => {
    const error = getRatingError(value);
    setRatingError(error);
    return error === '';
  };

  const validateQuantity = (value) => {
    const validation = getQuantityValidation(value, isPremium, freeMaxPuzzles);
    setQuantityError(validation.error);
    setShowQuantityTooltip(validation.showUpgrade);
    return validation.isValid;
  };

  const handleRatingChange = (event) => {
    const value = event.target.value;
    setRating(value);
    if (value === '') {
      setRatingError('');
    }
  };

  const handleRatingBlur = () => {
    validateRating(rating);
  };

  const handlePuzzleQuantityChange = (event) => {
    const value = event.target.value;
    setPuzzleQuantity(value);
    if (value === '') {
      setQuantityError('');
      setShowQuantityTooltip(false);
    } else {
      validateQuantity(value);
    }
  };

  const handlePuzzleQuantityBlur = () => {
    validateQuantity(puzzleQuantity);
  };

  // Derived: whether free user reached set limit
  const hasReachedSetLimit = !isPremium && Array.isArray(puzzleData) && puzzleData.length >= freeMaxSets;

  const handleSkillAssessmentComplete = async (rating, quantity, setId) => {
    try {
      if (!userEmail) {
        throw new Error('No email available');
      }

      // Navigate to briefing with initial data
      navigate('/woodpecker/briefing', {
        state: {
          setName: setName || '',
          setId: setId,
          puzzleIndex: 0,
          currentTime: 0,
          cycleNumber: 1,
          midCycle: false,
          solvedCorrectly: 0,
          userEmail: userEmail, 
          userRating: rating, 
          puzzleQuantity: quantity 
        } 
      });
    } catch (error) {
      console.error('Error in skill assessment completion:', error);
      alert('Failed to process login or load user data. Please try again.');
      navigate('/woodpecker');
    }
  };

  const handleSetSelection = async (set) => {
    try {
      if (!userEmail) {
        throw new Error('No email available');
      }

      // Navigate to briefing with initial data
      navigate('/woodpecker/briefing', { 
        state: { ...set, userEmail,}
      });
    } catch (error) {
      console.error('Error in loading puzzle :', error);
      alert('Failed load user data. Please try again.');
      navigate('/woodpecker');
    }
  };

  const handleConfirmDelete = async (setId) => {
    try {
      await lichessPuzzleApi.deleteTrainingSet(idToken, authProvider, userEmail, setId);
      // Optimistically update the shared puzzleData to remove the deleted set
      setPuzzleData((prev) => (Array.isArray(prev) ? prev.filter((s) => s.setId !== setId) : prev));
    } catch (error) {
      console.error('Error deleting training set:', error);
      alert('Failed to delete training set. Please try again.');
    } finally {
      setShowConfirmModal(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const isRatingValid = validateRating(rating);
    const isQuantityValid = validateQuantity(puzzleQuantity);

    if (!isRatingValid || !isQuantityValid) {
      return;
    }

    if (isAuthenticated) {
      handleSkillAssessmentComplete(parseInt(rating, 10), parseInt(puzzleQuantity, 10), uuidv4());
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const percent = (num, den) =>
    den && den > 0 ? Math.round(((num || 0) / den) * 100) : 0;

  const isLoadingSets = isAuthenticated && isPuzzleDataLoading;

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${woodpeckerSeoContent.canonical}#webpage`,
        url: woodpeckerSeoContent.canonical,
        name: woodpeckerSeoContent.title,
        description: woodpeckerSeoContent.description,
        dateModified: '2026-08-24',
        isPartOf: {
          '@type': 'WebSite',
          '@id': 'https://puzzlify.io/#website',
          url: 'https://puzzlify.io/',
          name: 'Puzzlify',
        },
        mainEntity: {
          '@id': `${woodpeckerSeoContent.canonical}#application`,
        },
        hasPart: {
          '@id': `${woodpeckerSeoContent.canonical}#faq`,
        },
      },
      {
        '@type': 'WebApplication',
        '@id': `${woodpeckerSeoContent.canonical}#application`,
        name: 'Puzzlify Woodpecker Method Trainer',
        url: woodpeckerSeoContent.canonical,
        description: woodpeckerSeoContent.description,
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Any',
        isAccessibleForFree: true,
        publisher: {
          '@type': 'Organization',
          name: 'Mountainsoft LLC',
          url: 'https://puzzlify.io/',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${woodpeckerSeoContent.canonical}#faq`,
        mainEntity: woodpeckerSeoContent.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  return (
    <div className="onboarding-page">
      <Helmet>
        <title>{woodpeckerSeoContent.title}</title>
        <meta
          name="description"
          content={woodpeckerSeoContent.description}
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link
          rel="canonical"
          href={woodpeckerSeoContent.canonical}
        />
        <meta property="og:title" content={woodpeckerSeoContent.title} />
        <meta
          property="og:description"
          content={woodpeckerSeoContent.description}
        />
        <meta property="og:url" content={woodpeckerSeoContent.canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <header className="-mt-5 flex w-full flex-wrap items-center justify-between gap-3">
        <Link to="/" className="inline-flex min-h-12 items-center gap-2 rounded-xl text-xl font-bold text-[#247da8] no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-700 focus-visible:ring-offset-2 sm:text-2xl" aria-label="Puzzlify home">
          <span className="inline-flex size-9 items-center justify-center rounded-full border border-sky-100 bg-white text-lg shadow-sm sm:size-11" aria-hidden="true">
            <FaChessQueen />
          </span>
          <span>Puzzlify</span>
        </Link>
        <Button variant="neutral" size="action" className="border-amber-200 bg-amber-50 px-3 text-sm text-amber-900 hover:bg-amber-100" aria-label={isPremium ? 'Premium Member' : 'Upgrade'} onClick={handleUpgradeClick}>
          <FaCrown aria-hidden="true" />
          {isPremium ? <span>Premium<span className="hidden sm:inline"> Member</span></span> : 'Upgrade'}
        </Button>
      </header>
      <section className="landing-intro">
        <h1>{woodpeckerSeoContent.heading}</h1>
        <p className="landing-intro-copy">
          {woodpeckerSeoContent.hero}
        </p>
      </section>

      <form onSubmit={handleSubmit} aria-labelledby="create-set-heading" className="relative mx-auto mb-8 w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm sm:p-6">
        {/* top accent bar */}
        <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-[#247da8]" />
        <h2 id="create-set-heading" className="mb-6 text-xl font-semibold text-slate-900">Create a training set to begin</h2>

        <div className="grid gap-5">
          {/* Set Name */}
          <div>
            <label htmlFor="setName" className="mb-1 block text-sm font-medium text-slate-700">
              Set Name
            </label>
            <input
              type="text"
              id="setName"
              value={setName}
              required
              onChange={(e) => setSetName(e.target.value)}
              placeholder="e.g., Intermediate Warmup Set"
              className={inputClassName}
            />
          </div>

          {/* Rating */}
          <div>
            <label htmlFor="rating" className="mb-1 block text-sm font-medium text-slate-700">
              Enter Your Puzzle Rating (400-3400)
            </label>
            <div className="relative">
              <input
                type="number"
                id="rating"
                value={rating}
                min={400}
                max={3400}
                onChange={handleRatingChange}
                onBlur={handleRatingBlur}
                placeholder="e.g., 1500"
                required
                className={inputClassName}
              />
              {ratingError && (
                <div className="mt-1 flex items-center text-sm text-rose-600">
                  <FaExclamationCircle className="mr-1" />
                  <span>{ratingError}</span>
                </div>
              )}
            </div>
          </div>

          {/* Puzzle Quantity */}
          <div>
            <label htmlFor="puzzleQuantity" className="mb-1 block text-sm font-medium text-slate-700">
              Number of Puzzles to Train With (5-1000)
            </label>
            <div className="relative">
              <input
                type="number"
                id="puzzleQuantity"
                value={puzzleQuantity}
                onChange={handlePuzzleQuantityChange}
                min={5}
                max={isPremium ? 1000 : freeMaxPuzzles}
                onBlur={(e) => {
                  handlePuzzleQuantityBlur();
                  if (parseInt(puzzleQuantity, 10) <= freeMaxPuzzles || isPremium) {
                    setShowQuantityTooltip(false);
                  }
                }}
                required
                className={inputClassName}
              />
              {showQuantityTooltip && !isPremium && (
                <div className="tooltip" style={{ width: 260 }}>
                  <FaCrown className="tooltip-crown" />
                  <p>Premium lets you create sets with more than {freeMaxPuzzles} puzzles.</p>
                  <button
                    type="button"
                    onClick={handleUpgradeClick}
                    className="tooltip-upgrade-button"
                  >
                    Upgrade Now
                  </button>
                </div>
              )}
              {quantityError && isPremium && (
                <div className="mt-1 flex items-center text-sm text-rose-600">
                  <FaExclamationCircle className="mr-1" />
                  <span>{quantityError}</span>
                </div>
              )}
            </div>
          </div>

          {/* Auth / Submit */}
          {!isAuthenticated ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-4 text-sm leading-relaxed text-slate-600">
                Please sign in with Google or Lichess to save your training progress
              </div>
              <div className="flex items-center justify-center gap-3">
                <GoogleLogin
                  type="icon"
                  size="large"
                  shape="square"
                  onSuccess={(credentialResponse) => handleGoogleLoginSuccess(credentialResponse, rating, puzzleQuantity)}
                  onError={handleGoogleLoginError}
                />
                <Button
                  variant="neutral"
                  size="icon"
                  className="h-[40px] w-[40px] rounded-[4px] p-0 shadow-none"
                  type="button"
                  aria-label="Sign in with Lichess"
                  title="Sign in with Lichess"
                  onClick={startLichessOAuth}
                >
                  <LichessLogo className="size-[18px]" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div
                className="relative w-full"
                onMouseEnter={() => hasReachedSetLimit && setShowSetLimitTooltip(true)}
                onMouseLeave={() => setShowSetLimitTooltip(false)}
              >
                <Button
                  variant="brand"
                  size="action"
                  type="submit"
                  disabled={hasReachedSetLimit}
                  className="w-full"
                >
                  Start Training
                </Button>
                {hasReachedSetLimit && showSetLimitTooltip && (
                  <div className="tooltip" style={{ width: 260 }}>
                    <FaCrown className="tooltip-crown" />
                    <p>Upgrade to Premium to create multiple training sets.</p>
                    <button
                      type="button"
                      className="tooltip-upgrade-button"
                      onClick={handleUpgradeClick}
                    >
                      Upgrade Now
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-2 text-sm text-slate-600 text-center">Hey {firstName}, enter your rating and puzzle quantity to create a set!</div>
            </div>
          )}
        </div>
      </form>

      {isLoadingSets ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
            <svg className="h-5 w-5 animate-spin text-slate-500" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
              <path
                className="opacity-75"
                d="M21 12a9 9 0 0 1-9 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900">Loading training sets</h3>
          <p className="mt-1 text-slate-600">Hang tight while we fetch your sets.</p>
        </div>
      ) : Array.isArray(puzzleData) && puzzleData.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
            <svg className="h-5 w-5 text-slate-500" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900">No training sets yet</h3>
          <p className="mt-1 text-slate-600">
            Create your first set to begin Woodpecker training.
          </p>
          {/* <button
            onClick={createNewTrainingSet}
            className="mt-6 inline-flex items-center rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-medium shadow-lg hover:shadow-slate-900/30 active:scale-[.98] transition"
          >
            Create Training Set
          </button> */}
        </div>
      ) : (
        /* Cards grid */
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {puzzleData.map((s) => {
            const setId = s.setId;
            const setName = s.setName;
            const completed = s.currentProgress?.puzzlesCompleted ?? 0;
            const total = s.puzzleQuantity ?? 0;
            const mistakesOnly = s.currentProgress?.sessionMode === 'mistakes';
            const roundTotal = mistakesOnly ? s.currentProgress.puzzleIds.length : total;
            const pct = percent(completed, roundTotal);
            const cycle = s.cycleNumber ?? 1;

            return (
              <div
                key={`${s.PK}-${s.SK}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
              >
                {/* top accent */}
                <div className="absolute inset-x-0 top-0 h-1 bg-[#247da8]" />

                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="text-xs uppercase tracking-wide text-slate-500">
                        Training Set
                      </div>
                      <h2 className="text-lg font-semibold text-slate-900">
                        {setName === '' || setName === undefined ? `Set ${setId + 1}` : setName}
                      </h2>
                    </div>

                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                      Cycle {cycle}
                    </span>
                  </div>

                  {/* Meta rows */}
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Puzzles</span>
                      <span className="font-medium text-slate-900">{total || "—"}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">{mistakesOnly ? 'Mistakes cycle' : 'Progress'}</span>
                      <span className="font-medium text-slate-900">
                        {completed}/{roundTotal} ({pct}%)
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Rating</span>
                      <span className="font-medium text-slate-900">
                        {s.userRating ?? "—"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Last updated</span>
                      <span className="pl-2 font-medium text-slate-700">
                        {formatDate(s.lastUpdated)}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="h-2 w-full rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-[#247da8] transition-all motion-reduce:transition-none"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-5 flex items-center gap-3">
                    <Button
                      variant="brand"
                      size="action"
                      onClick={() => handleSetSelection(s)}
                      className="flex-1 px-3 text-sm"
                      aria-label={`Continue set ${setId}`}
                    >
                      Continue
                      <svg className="ml-2 h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Button>

                    <Button
                      variant="neutral"
                      size="action"
                      onClick={() => { setSelectedSetId(setId); setShowConfirmModal(true); }}
                      className="size-12 p-0 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                      aria-label={`Delete set ${setId}`}
                      title="Delete set"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>

                {/* hover sheen */}
                <div className="pointer-events-none absolute inset-0 opacity-0 transition group-hover:opacity-100">
                  <div className="absolute -inset-x-10 -top-10 h-20 rotate-6 bg-white/40 blur-2xl" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => handleConfirmDelete(selectedSetId)}
        title="Delete Training Set?"
        message="Are you sure you want to delete this training set? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      <WoodpeckerMethodContent />

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        <Button variant="link" size="sm" onClick={() => setShowTermsModal(true)} className="m-0 h-11 border-0 bg-transparent px-2 text-xs font-normal text-slate-500 hover:bg-transparent hover:text-slate-700">
          Terms of Service
        </Button>
        <Button variant="link" size="sm" onClick={() => setShowPrivacyModal(true)} className="m-0 h-11 border-0 bg-transparent px-2 text-xs font-normal text-slate-500 hover:bg-transparent hover:text-slate-700">
          Privacy Policy
        </Button>
      </div>

      <footer className="onboarding-footer">
        <p>© 2025 Puzzlify. Operated by Mountainsoft LLC. All rights reserved.</p>
      </footer>

      <PricingModal 
        isOpen={showPricingModal} 
        onClose={() => setShowPricingModal(false)} 
      />

      <LegalModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Terms of Service"
        content={<TermsOfService />}
      />

      <LegalModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
        content={<PrivacyPolicy />}
      />
    </div>
  );
}

export default OnboardingPage;
